import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import PageTitle from 'components/PageTitle';
import { Alert, Button, Card, Col, ProgressBar, Row, Toast, ToastContainer } from 'react-bootstrap';
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import {
    Case,
    CaseLocation,
    SelectJob,
    SelectJobStatus
} from "helpers/interfaces";
import CaseApi from "api/case-api";
import { getDisplayDate, loading } from "helpers/utils";
import classNames from "classnames";
import SelectApi from "api/select-api";
import CameraMediaApi from "api/camera-media-api";
import VideoCreationApi from "api/video-creation-api";
import { ConfirmDialogBox } from "components/ConfirmDialogBox";
import SelectImages from "./SelectImages";
import HDImages from "./HDImages";
import Videos from "./Videos";
import AllImages from "./AllImages";
import CreateVideo from "./CreateVideo";
import { GalleryItem } from "helpers/types";
import { HDRequestStatus } from "helpers/interfaces";


const caseApi = new CaseApi();
const selectApi = new SelectApi()
const videoCreationApi = new VideoCreationApi()

// Page size used when jumping to a specific image, so the destination page loads a smaller, faster batch
const GO_TO_IMAGE_PAGE_SIZE = 48;

// Keep only characters that are safe inside a browser window/tab name
const sanitizeWindowNamePart = (value: any) =>
    String(value ?? '').replace(/[^a-zA-Z0-9_-]/g, '_');

// Stable tab name per case/location/date, so repeat "open in new tab" clicks reuse the same tab instead of piling up new ones
const getGoToImageTabName = (caseId: number, locationId: number, captureDate: string) =>
    `case-images-go-to:${sanitizeWindowNamePart(caseId)}:${sanitizeWindowNamePart(locationId)}:${sanitizeWindowNamePart(captureDate)}`;

type BannerNotification = {
    text: string;
    variant: string;
    [key: string]: any
}

type ToastNotification = {
    message: string;
    [key: string]: any
}

const DefaultSelectJob: any = {
    status: SelectJobStatus.CREATED,
    capture_date: "",
    location_id: 0,
    progress: 0,
    regions_of_interest: [],
    select_image_ids: [],
    threshold: 0
}

type ICaseImagesContext = {} & {
    selectImageIds: number[];
    lockedImageIds: number[];
    selectJob: SelectJob | any;
    caseInfo: Case | any,
    locationInfo: CaseLocation | any
    captureDate: string;
    onShowToastMessage?: (...args: any) => void;
    [key: string]: any
}
export const CaseImagesContext = createContext<ICaseImagesContext>({
    captureDate: "",
    caseInfo: null,
    locationInfo: null,
    selectImageIds: [],
    lockedImageIds: [],
    selectJob: null
});

const CaseImages = () => {
    const [selectedCase, setSelectedCase] = useState<Case | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const params = useParams();
    const caseId = Number(params.caseId);
    const locationId = Number(params.locationId);
    const captureDate: any = params.captureDate;
    const viewParam = params.view;
    const navigate = useNavigate();
    const location = useLocation();
    // Tracks the last go-to-image request we've already handled, so the URL effect below doesn't run it twice
    const handledGoToImageParamRef = useRef<string | null>(null);
    const [thresholdValue, setThresholdValue] = useState(0);

    const VALID_VIEWS = ['all', 'select', 'hd', 'create-video', 'video'] as const;
    const galleryName = (VALID_VIEWS.includes(viewParam as any) ? viewParam : 'all') as typeof VALID_VIEWS[number];

    const getViewPath = (view: string) => {
        const base = `/cases/${caseId}/locations/${locationId}/${captureDate}`;
        return view === 'all' ? base : `${base}/${view}`;
    };
    const [showAutoSelectConfirmation, setShowAutoSelectConfirmation] = useState(false);
    const [isCreatingSelectJob, setIsCreatingSelectJob] = useState(false);
    const [selectJob, setSelectJob] = useState<SelectJob | null>(null);
    const [startPollingSelectJob, setStartPollingSelectJob] = useState(false)
    const [selectJobPoll, setSelectJobPoll] = useState<any>();
    const [bannerNotifications, setBannerNotifications] = useState<{ [key: string]: BannerNotification }>({})
    const [locationInfo, setLocationInfo] = useState<CaseLocation | null>(null)
    const [toastMessages, setToastMessages] = useState<ToastNotification[]>([])
    const [showCancelJobConfirmation, setShowCancelJobConfirmation] = useState(false);
    const [isCancellingSelectJob, setIsCancellingSelectJob] = useState(false);
    const contextMenuContainer = useRef(null);
    const contextMenuContainerBottom = useRef(null);
    const [selectImageIds, setSelectImageIds] = useState<number[]>([]);
    const [lockedImageIds, setLockedImageIds] = useState<number[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    // Page size for the all-images view, initialized straight from the URL.
    // This covers EVERYTHING EXCEPT in-app navigation: any full page load — a brand-new tab, a reused tab,
    // or a reload (e.g. "Go to Image in New Tab") — boots CaseImages fresh, so reading the pageSize param here
    // means render #1 and the first fetch already use the requested size, instead of fetching at 240 and
    // correcting a beat later. The complementary in-app-navigation case (where CaseImages does NOT remount)
    // is handled by the AllImages mount effect.
    const [pageSize, setPageSize] = useState(() => {
        const requested = new URLSearchParams(window.location.search).get('pageSize');
        return requested ? (parseInt(requested, 10) || 240) : 240;
    });
    // When the tab boots with a go-to-image request, hold the initial gallery fetch until the
    // jump resolves its real page number. Otherwise page 1 loads first and its 48 image
    // downloads (never visible to the user) hog the connection pool and backend workers,
    // starving the jump page's images and any lock/HD requests made from its popup.
    const [holdPageFetchForGoToImage, setHoldPageFetchForGoToImage] = useState(() =>
        new URLSearchParams(window.location.search).get('goToImage') !== null
    );
    const [currentViewImageIndex, setCurrentViewImageIndex] = useState(0);
    const [roiPopupImage, setRoiPopupImage] = useState<any>();
    const [showRoiModal, setShowRoiModal] = useState(false);
    const [images, setImages] = useState<GalleryItem[]>([]);
    const [imageSequenceNumberToShowInPopup, setImageSequenceNumberToShowInPopup] = useState<Number>(-1);
    const [showGoToImageLoadingAnim, setShowGoToImageLoadingAnim] = useState(false)
    const [videoCreationImageSetImages, setVideoCreationImageSetImages] = useState<number[]>([])
    const [mediaFileRenamed, setMediaFileRenamed] = useState(0);
    
    

    const mediaApi = new CameraMediaApi();
    
    if (!captureDate) {
        navigate(`/cases/${caseId}/locations/${locationId}`)
    }

    // Redirect invalid view params to default (all)
    useEffect(() => {
        if (viewParam && !VALID_VIEWS.includes(viewParam as any)) {
            navigate(`/cases/${caseId}/locations/${locationId}/${captureDate}`, { replace: true });
        }
    }, [viewParam, caseId, locationId, captureDate, navigate]);

    const showNotificationBanner = useCallback((message: string, variant: string) => {
        setBannerNotifications(prevState => {
            const key = (Math.random() + 1).toString(36)
            const msg = {
                variant: variant,
                text: message
            }
            return {...prevState, [key]: msg}
        })
    }, []);

    const getImageSrc = (galleryIndex: number) => {
        //console.log('In getImageSrc func.');
        //console.log('In getImageSrc func. galleryIndex is ' + galleryIndex);
        return images[galleryIndex]?.image_src;

    }

    const isSelectImage = (imageId: number) => {
        return selectImageIds.includes(imageId);
    }

    // Ask the backend which page a target image falls on, for the given page size
    const goToImageDeterminePageNumber = async (target_image_seq_number: number, targetPageSize: number = pageSize) => {
        let page_num = null
        let resp = await caseApi.getFrontendPageForTargetImage(caseId, locationId, target_image_seq_number, {
            capture_date: captureDate,
            page_size: targetPageSize,
        })
        if (resp.go_to_page) {
            page_num = resp.go_to_page
        }
        return page_num
    }

    // Navigate to (and open) a specific image, loading its page at the requested page size
    const goToPageWithTargetImage = async (target_image_seq_number: number, switchToAllImagesGallery: boolean = false, targetPageSize: number = pageSize ) => {

        // Resize first so the destination page renders at the requested size
        setPageSize(targetPageSize)

        let page_num = null
        try {
            page_num = await goToImageDeterminePageNumber(target_image_seq_number, targetPageSize)
        } catch (e) {
            console.error(e)
        }

        if (page_num) {
            if (switchToAllImagesGallery === true) {
                navigate(getViewPath('all'));
            }
            // Mark which image to show; the ImageGallery effect opens its popup once that image is
            // present on the loaded page and decoded (covers both same-page and different-page targets).
            setCurrentPage(page_num)
            setImageSequenceNumberToShowInPopup(target_image_seq_number)
            // Real page known — release the go-to boot's hold so the gallery fetches it directly
            setHoldPageFetchForGoToImage(false)
        } else {
            showToastMessage && showToastMessage('Could not find image ' + target_image_seq_number)
            setShowGoToImageLoadingAnim(false)
            // Jump failed — release the hold so the gallery falls back to a normal page load
            setHoldPageFetchForGoToImage(false)
            return;
        }
    }

    // Jump to a typed image number (fired on Enter, not per keystroke): validate it,
    // then load the all-images view at the go-to page size
    const onGoToImage = (sequence: string) => {
        if (sequence.length < 1 || sequence.trim().length < 1) {
            return
        }
        let sequence_number_as_int = parseInt(sequence)
        if ( Number.isNaN(sequence_number_as_int) ) {
            showToastMessage('Image number is invalid.')
            // No jump coming — release a go-to boot's gallery fetch hold (no-op otherwise)
            setHoldPageFetchForGoToImage(false)
            return
        }

        setShowGoToImageLoadingAnim(true)
        goToPageWithTargetImage(sequence_number_as_int, galleryName !== 'all', GO_TO_IMAGE_PAGE_SIZE)
    }

    // Open the all-images view for this image in a separate, reusable browser tab
    const onGoToImageInNewTab = (sequence: string | number) => {
        // Encode the request in the URL, since the new tab shares no memory/state with this one
        const searchParams = new URLSearchParams({
            goToImage: String(sequence),       // which image to jump to
            goToTab: '1',                      // tells the new tab to claim the shared tab name
            goToRequest: String(Date.now()),   // unique id so repeat clicks on the same image still register
            pageSize: String(GO_TO_IMAGE_PAGE_SIZE) // page size the destination tab should load the gallery at
        });

        // Naming the tab makes window.open reuse the existing tab instead of spawning a new one each time
        const tabName = getGoToImageTabName(caseId, locationId, captureDate);
        const goToPageTab = window.open(
            `${window.location.origin}${getViewPath('all')}?${searchParams.toString()}`,
            tabName
        );
        goToPageTab?.focus();
    }

    // When a tab loads (or its URL changes) with go-to-image params, jump to that image once
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sequence = params.get('goToImage')?.trim();
        const requestId = params.get('goToRequest')?.trim();
        const shouldMarkGoToTab = params.get('goToTab') === '1';

        // Skip if there's no request, or we've already handled this exact one
        const dedupeKey = requestId || sequence;
        if (!sequence || !dedupeKey || handledGoToImageParamRef.current === dedupeKey) {
            return;
        }

        handledGoToImageParamRef.current = dedupeKey;

        // Claim the shared tab name so future "open in new tab" clicks reuse this tab
        if (shouldMarkGoToTab) {
            window.name = getGoToImageTabName(caseId, locationId, captureDate);
        }

        // onGoToImage is no longer debounced, so the URL boot can share its validation and jump
        onGoToImage(sequence);
    }, [location.search]);

    const caseImagesAreWeOnApageWhereSelectRibbonShouldShow = (page_name: string) => {
        if (page_name  === 'Create Video') {
            return false;
        } 
        return true;
    }

    const isImageInCreateVideoImageSet = (imageId: number) => {
        return videoCreationImageSetImages.includes(imageId);
    }

    const getImageCornerRibbonMarkup = (ribbon_text: string = 'Select') => {
        return (
            <div className="select-ribbon select-ribbon-primary">
                <span>{ribbon_text}</span>
            </div>
        )
    }

    const singeImageCheckboxHandlerCreateVideoPage = (imageId: number) => {
        setVideoCreationImageSetImages((prevIds: number[]) => {
            const updatedIds = [...prevIds]
            if (!updatedIds.includes(imageId)) {
                updatedIds.push(imageId);
            } else {
                const indx = updatedIds.findIndex(id => id === imageId);
                updatedIds.splice(indx, 1);
            }
            return [...updatedIds]
        })
    }

    const imageIsPrimaryCheckboxChecked = (imageId: number, page_name: string) => {
        if (caseImagesAreWeOnApageWhereSelectRibbonShouldShow(page_name) && isSelectImage(imageId)){
            return true;
        } else if (page_name === 'Create Video' && isImageInCreateVideoImageSet(imageId)){
            return true;
        }
        return false;
    }


    // Fetch Case
    useEffect(() => {
        const fetchCaseInfo = async () => {
            setIsLoading(true);
            try {
                const _case = await caseApi.fetchCase(caseId)
                setSelectedCase(_case);
            } catch (e) {
                console.log("Error fetching data", e);
            } finally {
                setIsLoading(false);
            }
        };
        fetchCaseInfo();
    }, [caseId]);

    // Fetch Location info
    useEffect(() => {
        const fetchLocationInfo = async () => {
            try {
                setIsLoading(true);
                const location = await caseApi.getLocation(caseId, locationId)
                setLocationInfo(location);

            } catch (e) {
                console.error(e)
            } finally {
                setIsLoading(false)
            }
        };

        fetchLocationInfo();
    }, [caseId, locationId]);

    // Clear the previous dayfolder's job on switch (no remount happens) so stale ids
    // can't be saved into the new one. Not in fetchLatestSelectJob — that's polled
    // every 3s during a running job, and nulling the job each tick flickers the UI.
    useEffect(() => {
        setSelectJob(null);
        setSelectImageIds([]);
    }, [locationId, captureDate]);

    const fetchLatestSelectJob = useCallback(async () => {
        try {
            let selectJob = await selectApi.getLatestSelectJob(locationId, captureDate);
            if (Object.keys(selectJob).length === 0) {
                selectJob = DefaultSelectJob as SelectJob;
            }
            setSelectJob(selectJob)
            const selectIds = selectJob ? selectJob.select_image_ids : [];
            setSelectImageIds(selectIds)
            const threshold = Math.floor(selectJob.threshold / 255 * 100)
            setThresholdValue(threshold)

            // Start/Stop poll when status changes
            if ([SelectJobStatus.STARTED, SelectJobStatus.IN_PROGRESS].includes(selectJob.status)) {
                setStartPollingSelectJob(true);
            } else {
                setStartPollingSelectJob(false);
            }
        } catch (e) {
            console.error(e);
        }
    }, [captureDate, locationId]);

    useEffect(() => {
        fetchLatestSelectJob();
    }, [fetchLatestSelectJob]);


    // Fetch locked images
    useEffect(() => {
        const fetchLockedImages = async () => {
            try {
                setIsLoading(true);
                let lockedImages = await mediaApi.getLockedMediaItems(locationId, captureDate );
                setLockedImageIds(lockedImages.locked_image_ids);
            } catch (e) {
                console.log('there seems to be an error with fetching locked images.');
                console.error(e);
            } finally {
                setIsLoading(false);
            }
        }
        fetchLockedImages();
    }, [locationId, captureDate]);


    // fetch video creation images 
    useEffect( () => {
        if ( galleryName === 'create-video' ) {
            const fetchVideoCreationImages = async () => {
                try {
                    setIsLoading(true);
                    let videoCreationImages = await videoCreationApi.getVideoCreationImageSet(locationId, captureDate );
                    if ('video_creation_imgs' in videoCreationImages) {
                        setVideoCreationImageSetImages(videoCreationImages.video_creation_imgs)
                    }
                } catch (e) {
                    console.log('there seems to be an error with fetching video-creation images.');
                    console.error(e);
                } finally {
                    setIsLoading(false);
                }
            }
            fetchVideoCreationImages();
        }
    }, [locationId, captureDate, galleryName])



    // Start/Stop select job poll
    useEffect(() => {
        if (!selectJob) {
            return;
        }
        // Start polling
        if (!selectJobPoll && startPollingSelectJob) {
            const interval = setInterval(() => fetchLatestSelectJob(), 3000) // Poll every 3 seconds
            setSelectJobPoll(interval);
            return;
        }
        // Stop polling
        if (selectJobPoll && !startPollingSelectJob) {
            clearInterval(selectJobPoll);
            setSelectJobPoll(null);

            if (selectJob.status === SelectJobStatus.COMPLETED) {
                const message = `Auto select job completed with ${selectJob.select_image_ids.length} selections`;
                showNotificationBanner(message, "success")
            }
            if (selectJob.status === SelectJobStatus.FAILED) {
                const message = "Auto select job failed.";
                showNotificationBanner(message, "danger")
            }
        }

    }, [startPollingSelectJob, selectJobPoll, selectJob, fetchLatestSelectJob, showNotificationBanner]);

    const onThresholdValueChanged = (e: any) => {
        setThresholdValue(e.target.value);
    }

    const createAutoSelectJob = async () => {
        try {
            setIsCreatingSelectJob(true);
            const selectJob = await selectApi.createSelectJob({
                location_id: locationId,
                capture_date: captureDate,
                threshold: thresholdValue
            });
            setSelectJob(selectJob);
            setStartPollingSelectJob(true);
            const message = "Auto select job has been started"
            showNotificationBanner(message, "info")
        } catch (e: any) {
            console.log(e)
            const msg = e?.data?.error || "An error occurred while performing action"
            showNotificationBanner(msg, "danger")
        } finally {
            setIsCreatingSelectJob(false);
            toggleAutoSelectConfirmation();
            setVideoCreationImageSetImages([])
        }
    }

    const toggleAutoSelectConfirmation = () => {
        setIsCreatingSelectJob(false);
        setShowAutoSelectConfirmation(!showAutoSelectConfirmation);
    }

    const onCloseNotification = (key: string) => {
        setBannerNotifications(prevState => {
            delete prevState[key]
            return prevState
        });
    }

    const createManualSelectJob = async (imageIds: number[]) => {
        try {
            setIsCreatingSelectJob(true);
            const job = await selectApi.createSelectJob({
                location_id: locationId,
                capture_date: captureDate,
                select_type: "manual",
                select_image_ids: imageIds
            });
            setSelectJob(job);
            const message = "Select saved."
            showNotificationBanner(message, "info")
        } catch (e: any) {
            console.log(e);
            const msg = e?.data?.error || "Could not save the selection"
            showNotificationBanner(msg, "danger")
        } finally {
            setIsCreatingSelectJob(false);
            setVideoCreationImageSetImages([])
        }
    }

    const addImagesToVideoImageSet = async (imageIds: number[]) => {

    }

    const onCloseToast = (index: number) => {
        const list = [...toastMessages];
        list.splice(index, 1);
        setToastMessages(list);
    };

    const cancelSelectJob = async () => {
        try {
            setIsCancellingSelectJob(true);
            const job = await selectApi.cancelSelectJob(selectJob?.id);
            setSelectJob(job)
            // Cancel polling
            setStartPollingSelectJob(false);
            setShowCancelJobConfirmation(false);

            const message = "Auto select job has been cancelled";
            showNotificationBanner(message, "danger");
        } catch (e) {
            console.error("Error cancelling auto select")
        } finally {
            setIsCancellingSelectJob(false);
        }
    }

    const showToastMessage = useCallback((message: string, type = "warning") => {
        setToastMessages(prevState => {
            const msg: ToastNotification = {
                message: message,
                type: type
            }
            const newMessages = [...prevState, msg];
            return newMessages;
        });
    }, []);

    const getImageGallery = (name: string) => {
        switch (name) {
            case 'all':
                return <AllImages/>;
            case 'select':
                return <SelectImages/>;
            case 'hd':
                return <HDImages/>;
            case 'create-video':
                return <CreateVideo/>;
            case 'video':
                return <Videos/>;
        }
    }

    const imageRequestHD = async (
        index: number, 
        imageId: number, 
        paramImages: GalleryItem[], 
        paramSetImages: (arg1: GalleryItem[]) => void, 
        paramMediaApi: CameraMediaApi
    ) => {

        const image = paramImages[index]
        if (image.hd_request_status === HDRequestStatus.PENDING || image.hd_file_path !== null) {
            showToastMessage && showToastMessage('HD already requested')
            return;
        }
        if (image.hd_request_status === HDRequestStatus.COMPLETED) {
            showToastMessage && showToastMessage('HD received', "info")
            return;
        }
        try {
            const hdRequest = await paramMediaApi.requestHDImage(imageId)
            image.hd_request_status = hdRequest.status;
            image.hd_file_path = hdRequest.filepath;
            paramImages[index] = image
            paramSetImages([...paramImages])
        } catch (e) {
            console.log(e);
        }
    }


    const onHideRoiModal = () => {
        setRoiPopupImage(null);
        setShowRoiModal(false);
    }

    const openLightBox = (index: number, imageSrc: string) => {
        setCurrentViewImageIndex(index);
        onShowRoiModal(imageSrc)
    }

    const onShowRoiModal = (imageSrc?: string) => {
        setRoiPopupImage(imageSrc)
        setShowRoiModal(true);
    };

    // Toolbar rendered both above and below the gallery. Each instance needs its own
    // portal-container ref (a ref can hold only one DOM node) and a unique slider id.
    const renderGalleryToolbar = (position: 'top' | 'bottom') => (
        <Card>
            <Card.Body>
                {isLoading && loading()}
                <Row>
                    <Col md={6} >
                        <div className="gallery-filter-menu">
                            <Link
                                to={getViewPath('all')}
                                className={classNames('filter-menu-item', 'me-1', {
                                    active: galleryName === 'all',
                                })}
                            > View All
                            </Link>
                            <Link
                                to={getViewPath('select')}
                                className={classNames('filter-menu-item', 'me-1', {
                                    active: galleryName === 'select',
                                })}
                            >Select Images
                            </Link>
                            <Link
                                to={getViewPath('hd')}
                                className={classNames('filter-menu-item', 'me-1', {
                                    active: galleryName === 'hd',
                                })}
                            >HD
                            </Link>
                            <Link
                                to={getViewPath('create-video')}
                                className={classNames('filter-menu-item', 'me-1', {
                                    active: galleryName === 'create-video',
                                })}
                            >Create Video
                            </Link>
                            <Link
                                to={getViewPath('video')}
                                className={classNames('filter-menu-item', 'me-1', {
                                    active: galleryName === 'video',
                                })}
                            >Video
                            </Link>
                            {/* Hidden until this dayfolder's select job has loaded — a click before
                                then would save the uninitialized (empty) selection over the real one.
                                Compares contents, not just counts, so equal-count swaps stay savable. */}
                            {(isCreatingSelectJob ||
                                    (selectJob !== null &&
                                        (selectImageIds.length !== selectJob.select_image_ids.length ||
                                            selectImageIds.some(id => !selectJob.select_image_ids.includes(id))))
                                ) &&
                                <Button
                                    disabled={isCreatingSelectJob}
                                    onClick={() => createManualSelectJob(selectImageIds)}
                                    variant={"secondary"}>
                                    {isCreatingSelectJob && <div
                                        className="spinner-border spinner-border-sm me-1"/>}
                                    Save Selected Images
                                </Button>
                            }
                            <span ref={position === 'top' ? contextMenuContainer : contextMenuContainerBottom}></span>
                        </div>
                    </Col>
                    <Col md={6} >
                        <div className="d-flex align-items-center justify-content-end gap-2">
                            {
                                !selectJob || ![SelectJobStatus.STARTED, SelectJobStatus.IN_PROGRESS].includes(selectJob?.status) ? (
                                        <>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="form-label">Threshold</span>
                                                <input
                                                    type="range"
                                                    className="form-range d-inline"
                                                    min="0"
                                                    max="100"
                                                    step={2}
                                                    value={thresholdValue}
                                                    onChange={onThresholdValueChanged}
                                                    id={`threshold-${position}`}/>
                                                <span>{thresholdValue}</span>
                                            </div>
                                            <Button
                                                onClick={toggleAutoSelectConfirmation}
                                                variant={"primary"}>Auto Select Images
                                            </Button>
                                        </>) :
                                    <div className="d-flex align-items-center gap-2">
                                        <span
                                            className="text-primary ">In progress</span>
                                        <div className='progress-bar'>
                                            {(selectJob?.progress) > -1 && <ProgressBar
                                                className={"select-progress"}
                                                striped variant="primary"
                                                now={selectJob?.progress}
                                                label={`${selectJob?.progress}%`} animated={true}/>}
                                        </div>
                                        <Button
                                            onClick={() => setShowCancelJobConfirmation(true)}
                                            variant={"secondary"}>Cancel
                                        </Button>
                                    </div>
                            }
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );

    return (
        <React.Fragment>
            <PageTitle
                breadCrumbItems={[
                    {label: "Cases", path: "/cases"},
                    {label: "View Cases", path: "/cases"},
                    {label: selectedCase?.name, path: `/cases/${caseId}`},
                    {label: `${locationInfo?.name}`, path: `/cases/${caseId}/locations/${locationId}`},
                    {
                        label: getDisplayDate(captureDate),
                        path: `/cases/${caseId}/locations/${locationId}/${captureDate}`,
                        active: true
                    }
                ]}
                title={getDisplayDate(captureDate)}
            />
            {Object.keys(bannerNotifications).length > 0 &&
                <Row>
                    <Col>
                        {Object.entries(bannerNotifications).map(([itemKey, item], idx) => {
                            return (
                                <Alert
                                    key={idx}
                                    variant={item.variant || "primary"}
                                    onClose={() => onCloseNotification(itemKey)}
                                    dismissible>
                                    {item.text}
                                </Alert>
                            )
                        })
                        }
                    </Col>
                </Row>
            }

            {renderGalleryToolbar('top')}
            {selectedCase && locationInfo && captureDate &&
                <CaseImagesContext.Provider
                    value={{
                        selectImageIds,
                        lockedImageIds,
                        setSelectImageIds,
                        setLockedImageIds,
                        setLocationInfo,
                        selectJob,
                        captureDate,
                        contextMenuContainer,
                        contextMenuContainerBottom,
                        locationInfo,
                        caseInfo: selectedCase,
                        onShowToastMessage: showToastMessage,
                        showNotificationBanner,
                        imageRequestHD,
                        currentPage,
                        setCurrentPage,
                        goToPageWithTargetImage,
                        pageSize,
                        setPageSize,
                        holdPageFetchForGoToImage,
                        currentViewImageIndex,
                        setCurrentViewImageIndex,
                        roiPopupImage,
                        setRoiPopupImage,
                        showRoiModal,
                        setShowRoiModal,
                        onHideRoiModal,
                        onShowRoiModal,
                        openLightBox,
                        images,
                        setImages,
                        getImageSrc,
                        imageSequenceNumberToShowInPopup,
                        setImageSequenceNumberToShowInPopup, 
                        onGoToImage,
                        onGoToImageInNewTab,
                        setShowGoToImageLoadingAnim,
                        showGoToImageLoadingAnim,
                        videoCreationImageSetImages,
                        setVideoCreationImageSetImages,
                        caseImagesAreWeOnApageWhereSelectRibbonShouldShow,
                        isImageInCreateVideoImageSet,
                        getImageCornerRibbonMarkup,
                        singeImageCheckboxHandlerCreateVideoPage,
                        isSelectImage,
                        imageIsPrimaryCheckboxChecked,
                        mediaFileRenamed,
                        setMediaFileRenamed
                    }}>
                    {
                        getImageGallery(galleryName)
                    }
                </CaseImagesContext.Provider>
            }

            {selectedCase && locationInfo && captureDate && renderGalleryToolbar('bottom')}

            <ConfirmDialogBox
                title="Auto-Select Images?"
                show={showAutoSelectConfirmation}
                onHide={toggleAutoSelectConfirmation}
                onConfirm={createAutoSelectJob}
                isLoading={isCreatingSelectJob}
                onCancel={toggleAutoSelectConfirmation}>
                <p>Do you want to auto select images?</p>
                {selectJob && selectJob.select_image_ids.length > 0 && <span
                    className="fw-bold text-danger"
                >NB: This will erase previous selections</span>}
            </ConfirmDialogBox>

            <ConfirmDialogBox
                title="Cancel Auto-Select ?"
                actionInProgress={isCancellingSelectJob}
                onHide={() => setShowCancelJobConfirmation(!showCancelJobConfirmation)}
                onCancel={() => setShowCancelJobConfirmation(!showCancelJobConfirmation)}
                onConfirm={() => cancelSelectJob()}
                confirmBtnVariant="danger"
                show={showCancelJobConfirmation}>
                <span>Are you sure you want to cancel this job?</span>
            </ConfirmDialogBox>

            <ToastContainer className="p-3 position-fixed bottom-0 start-0">
                {toastMessages.map((item: ToastNotification, index) => {
                    return (
                        <Toast
                            key={index}
                            onClose={() => onCloseToast(index)}
                            delay={5000}
                            autohide
                        >
                            <Toast.Header className={`bg-${item.type} text-white`}>
                                <span className="material-symbols-outlined">info</span>
                                <strong className="ms-2 me-auto">Notification Message</strong>
                            </Toast.Header>
                            <Toast.Body className="bg-white">{item.message}</Toast.Body>
                        </Toast>
                    );
                })}
            </ToastContainer>
        </React.Fragment>
    )
}

export default CaseImages
